import {css} from 'aphrodite';
import classNames from 'classnames';

import SequenceAlignmentTabOptionsView from 'analyses/alignment/SequenceAlignmentTabOptionsView';
import AnnotationsTableTabState from 'analyses/AnnotationsTableTabState';
import {AnnotationsTableTabView} from 'analyses/AnnotationsTableView';
import DigestTabState from 'analyses/DigestTabState';
import * as DigestView from 'analyses/DigestView';
import EditPrimerTabState from 'analyses/EditPrimerTabState';
import * as EditPrimerView from 'analyses/EditPrimerView';
import Primer3TabState from 'analyses/Primer3TabState';
import * as Primer3View from 'analyses/Primer3View';
import * as PrimerWizardTab from 'analyses/primers/PrimerWizardTab';
import PrimerWizardTabState from 'analyses/primers/PrimerWizardTabState';
import SecondaryStructureTabState from 'analyses/SecondaryStructureTabState';
import * as SecondaryStructureView from 'analyses/SecondaryStructureView';
import TranslationTabState from 'analyses/TranslationTabState';
import {TranslationTabView} from 'analyses/TranslationView';
import VirtualDigestTabState from 'analyses/VirtualDigestTabState';
import * as VirtualDigestView from 'analyses/VirtualDigestView';
import AnalysisTabState from 'analysis-tool/backbone/AnalysisTabState';
import AnalysisTabView from 'analysis-tool/backbone/AnalysisTabView';
import AnalysisTemplateTabState from 'analysis-tool/templates/AnalysisTemplateTabState';
import AnalysisTemplateTabView from 'analysis-tool/templates/AnalysisTemplateTabView';
import AntibodyMap from 'antibodies/antibody-map/AntibodyMap';
import AntibodyMapTabOptions from 'antibodies/antibody-map/AntibodyMapTabOptions';
import AntibodyMapTabState from 'antibodies/antibody-map/AntibodyMapTabState';
import ProteinEntityStructureTabState from 'antibodies/structure-prediction/ProteinEntityStructureTabState';
import ProteinEntityStructureTabView, {
  ProteinEntityStructureTabViewProps,
} from 'antibodies/structure-prediction/ProteinEntityStructureTabView';
import JellyfishTabState from 'app-core/archetype/workspace/JellyfishTabState';
import JellyfishView from 'app-core/archetype/workspace/JellyfishView';
import GibsonTabState from 'assembly/tabs/GibsonTabState';
import * as GibsonTabView from 'assembly/tabs/GibsonTabView';
import GoldenGateTabState from 'assembly/tabs/GoldenGateTabState';
import * as GoldenGateTabView from 'assembly/tabs/GoldenGateTabView';
import BenchlingAppTabState from 'benchling-apps/backbone/BenchlingAppTabState';
import BenchlingAppDetails, {
  BENCHLING_APP_BUFFER_TAB_STYLES,
} from 'benchling-apps/end-user-page/BenchlingAppDetails';
import {BufferViewMetadata, BufferViewType} from 'buffers/BufferTypes';
import CanvasDefinitionTabState from 'canvas-core/backbone/CanvasDefinitionTabState';
import CanvasDefinitionTabView from 'canvas-core/backbone/CanvasDefinitionTabView';
import CanvasFolderArtifactTabState from 'canvas-core/backbone/CanvasFolderArtifactTabState';
import CanvasFolderArtifactView from 'canvas-core/view/CanvasFolderArtifactView';
import MoleculeChemicalStructureTab from 'chemistry/molecules/tabs/MoleculeChemicalStructureTab';
import MoleculeChemicalStructureTabState from 'chemistry/molecules/tabs/MoleculeChemicalStructureTabState';
import MoleculePhysicochemicalPropertiesTab from 'chemistry/molecules/tabs/MoleculePhysicochemicalPropertiesTab';
import MoleculePhysicochemicalPropertiesTabState from 'chemistry/molecules/tabs/MoleculePhysicochemicalPropertiesTabState';
import MoleculePredictedPropertiesTab from 'chemistry/molecules/tabs/MoleculePredictedPropertiesTab';
import MoleculePredictedPropertiesTabState from 'chemistry/molecules/tabs/MoleculePredictedPropertiesTabState';
import DatasetTabState from 'data-frames/workspace/DatasetTabState';
import DatasetView from 'data-frames/workspace/DatasetView';
import {
  ExternalEntryProtocolOptionsView,
  ExternalEntryProtocolTabView,
} from 'external-files/ExternalEntryProtocolTab';
import FileTabState from 'files/workspace/FileTabState';
import FileView from 'files/workspace/FileView';
import EditFormDefinitionPage from 'forms/form-definition/EditFormDefinitionPage';
import FormDefinitionTabState from 'forms/form-definition/FormDefinitionTabState';
import AnalyticsDashboardTabState from 'insights/analytics-dashboards/backbone/AnalyticsDashboardTabState';
import AnalyticsDashboardDetails from 'insights/analytics-dashboards/details/AnalyticsDashboardDetails';
import {DEFAULT_DASHBOARD_DETAILS_CONTEXT} from 'insights/analytics-dashboards/details/AnalyticsDashboardDetailsContext';
import EmbeddedAnalyticsDashboardTabState from 'insights/embeddings/backbone/EmbeddedAnalyticsDashboardTabState';
import EmbeddedAnalyticsDashboardView from 'insights/embeddings/backbone/EmbeddedAnalyticsDashboardView';
import ConnectionDataTabState from 'instruments/connections/connection-editor/ConnectionDataTabState';
import ConnectionDataTabView from 'instruments/connections/connection-editor/ConnectionDataTabView';
import ConnectionDetailsTabState from 'instruments/connections/connection-editor/ConnectionDetailsTabState';
import ConnectionDetailsTabView from 'instruments/connections/connection-editor/ConnectionDetailsTabView';
import ConnectionRunsTabState from 'instruments/connections/connection-editor/ConnectionRunsTabState';
import ConnectionRunsTabView from 'instruments/connections/connection-editor/ConnectionRunsTabView';
import AgentFoundryPage from 'llm/chat-library/foundries/AgentFoundryPage';
import AgentFoundryTabState from 'llm/chat-library/foundries/backbone/AgentFoundryTabState';
import MetadataTabState from 'metadata/MetadataTabState';
import MetadataTabView from 'metadata/MetadataTabView';
import ComplexPolymerComponentsTableTab from 'modbio/complex-polymers/tabs/ComplexPolymerComponentsTableTab';
import ComplexPolymerComponentsTabState from 'modbio/complex-polymers/tabs/ComplexPolymerComponentsTabState';
import ComplexPolymerMap from 'modbio/complex-polymers/tabs/ComplexPolymerMap';
import ComplexPolymerMapTabState from 'modbio/complex-polymers/tabs/ComplexPolymerMapTabState';
import GenerativeModelsTabState from 'model-hub/generative-models/GenerativeModelsTabState';
import GenerativeModelsTabView from 'model-hub/generative-models/GenerativeModelsTabView';
import AaSequenceAnnotationsTableTabState from 'molbio/aa-sequences/AaSequenceAnnotationsTableTabState';
import AaSequenceExpandedAnnotationTab from 'molbio/aa-sequences/AaSequenceExpandedAnnotationTab';
import AaSequenceAlignmentTabOptionsView from 'molbio/aa-sequences/alignments/AaSequenceAlignmentTabOptionsView';
import AlphaFoldOptionsView, {
  AlphaFoldOptionsProps,
} from 'molbio/aa-sequences/alphafold/AlphaFoldOptionsView';
import AlphaFoldTabState from 'molbio/aa-sequences/alphafold/AlphaFoldTabState';
import AlphaFoldTabView, {AlphaFoldTabContentProps} from 'molbio/aa-sequences/alphafold/AlphaFoldTabView';
import {
  BiochemicalPropertiesTabView,
  AaSequenceBiochemicalPropertiesTabState,
  TranslationBiochemicalPropertiesTabState,
} from 'molbio/aa-sequences/BiochemicalPropertiesTab';
import AaSequenceComplexPreview from 'molbio/aa-sequences/complex/AaSequenceComplexPreview';
import * as AaSequenceComplexPreviewTab from 'molbio/aa-sequences/complex/AaSequenceComplexPreviewTab';
import AaSequenceComplexProperties from 'molbio/aa-sequences/complex/AaSequenceComplexProperties';
import * as AaSequenceComplexPropertiesTab from 'molbio/aa-sequences/complex/AaSequenceComplexPropertiesTab';
import BulkAssemblyConstructsTabState from 'molbio/bulk-assembly/backbone/BulkAssemblyConstructsTabState';
import BulkAssemblyOverviewTabState from 'molbio/bulk-assembly/backbone/BulkAssemblyOverviewTabState';
import BulkAssemblyOverviewTabView from 'molbio/bulk-assembly/backbone/BulkAssemblyOverviewTabView';
import BulkAssemblyConstructsTab from 'molbio/bulk-assembly/constructs-tab/BulkAssemblyConstructsTab';
import CodonOptimizationTabState from 'molbio/codon-optimization/CodonOptimizationTabState';
import CodonOptimizationTabView from 'molbio/codon-optimization/CodonOptimizationTabView';
import CRISPRAssemblyTabState from 'molbio/crispr/assembly/CRISPRAssemblyTabState';
import {
  ConnectedCRISPRAssemblyTab,
  CRISPRAssemblyOptions,
} from 'molbio/crispr/assembly/CRISPRAssemblyTabView';
import CRISPRAnalysisTabState from 'molbio/crispr/CRISPRAnalysisTabState';
import {CRISPRAnalysisOptionsView, CRISPRAnalysisTabView} from 'molbio/crispr/CRISPRAnalysisTabView';
import HRTemplateTabState from 'molbio/crispr/hr-template/HRTemplateTabState';
import * as HRTemplateTabView from 'molbio/crispr/hr-template/HRTemplateTabView';
import {FindPrimersOptionsView} from 'molbio/find-primers/FindPrimersOptionsView';
import {FindPrimersTabState} from 'molbio/find-primers/FindPrimersTabState';
import FindPrimersView from 'molbio/find-primers/ReactFindPrimersTabView';
import AaSequenceAlignmentTabView from 'molbio/maps/alignment/AaSequenceAlignmentTabView';
import SequenceAlignmentTabView from 'molbio/maps/alignment/SequenceAlignmentTabView';
import {CircularMapTabView} from 'molbio/maps/circular/Circular';
import * as AaSequenceMapView from 'molbio/maps/linear/AaSequenceMapView';
import {SequenceLinearMapTabView} from 'molbio/maps/linear/Linear';
import {MiniMapTabView} from 'molbio/maps/linear/MiniMap';
import {CircularMapTabOptionsView} from 'molbio/maps/options/CircularSequenceOptionsView';
import {LinearMapTabOptionsView} from 'molbio/maps/options/LinearSequenceOptionsView';
import {MiniMapTabOptionsView} from 'molbio/maps/options/MiniMapSequenceOptionsView';
import NucleotidesTabState from 'molbio/monomers/NucleotidesTabState';
import NucleotidesTabView from 'molbio/monomers/NucleotidesTabView';
import AaSequenceAlignmentTabState from 'molbio/tabstates/AaSequenceAlignmentTabState';
import AaSequenceMapTabState from 'molbio/tabstates/AaSequenceMapTabState';
import CircularMapTabState from 'molbio/tabstates/CircularMapTabState';
import MiniMapTabState from 'molbio/tabstates/MiniMapTabState';
import OligoLinearMapTabState from 'molbio/tabstates/OligoLinearMapTabState';
import RNASequenceLinearMapTabState from 'molbio/tabstates/RNASequenceLinearMapTabState';
import RNASequenceMiniMapTabState from 'molbio/tabstates/RNASequenceMiniMapTabState';
import SequenceAlignmentTabState from 'molbio/tabstates/SequenceAlignmentTabState';
import SequenceLinearMapTabState from 'molbio/tabstates/SequenceLinearMapTabState';
import AssemblyHistoryTabState from 'molbio/visual-history/assembly-history/AssemblyHistoryTabState';
import AssemblyHistoryTabView from 'molbio/visual-history/assembly-history/AssemblyHistoryTabView';
import VisualHistoryTabState from 'molbio/visual-history/VisualHistoryTabState';
import VisualHistoryTabView from 'molbio/visual-history/VisualHistoryTabView';
import {TabState} from 'Navigation';
import * as BlobLinkTab from 'notebook/data-tables/BlobLinkTab';
import NoteLinkedEmbeddingTab from 'notebook/embeddings/NoteLinkedEmbeddingTab';
import EmptyEntryTab from 'notebook/entries/backbone/tabs/EmptyEntryTab';
import {EmptyEntryTabState} from 'notebook/entries/backbone/tabs/EmptyEntryTabState';
import EntryProtocolTabState from 'notebook/entries/backbone/tabs/EntryProtocolTab';
import * as EntryProtocolView from 'notebook/entries/backbone/tabs/EntryProtocolView';
import {ExternalEntryProtocolTabState} from 'notebook/entries/backbone/tabs/ExternalEntryProtocol';
import {
  NewEntryProtocolTabState,
  NewEntryProtocolTabView,
} from 'notebook/entries/backbone/tabs/NewEntryProtocolTab';
import * as NoteLinkedFileTab from 'notebook/notes/attachments/NoteLinkedFileTab';
import NoteTabState from 'notebook/notes/tabs/NoteTabState';
import NoteTabView from 'notebook/notes/tabs/NoteTabView';
import ProtocolTabState from 'notebook/protocols/backbone/tabs/ProtocolTabState';
import * as ProtocolView from 'notebook/protocols/backbone/tabs/ProtocolTabView';
import ReviewHistoryTabState from 'notebook/reviews/ReviewHistoryTabState';
import ReviewHistoryTabView from 'notebook/reviews/ReviewHistoryTabView';
import ReviewTabState from 'notebook/reviews/ReviewTabState';
import {ReviewTabView} from 'notebook/reviews/ReviewView';
import WorksheetTabState from 'notebook/worksheets/workspace/WorksheetTabState';
import WorksheetTabView from 'notebook/worksheets/workspace/WorksheetTabView';
import PlateDesignTemplatePage from 'plates/plate-design-templates/PlateDesignTemplatePage';
import PlateDesignTemplateTabState from 'plates/plate-design-templates/PlateDesignTemplateTabState';
import ProcedureTabState from 'procedures/ProcedureTabState';
import ProcedureTabView from 'procedures/ProcedureTabView';
import ProcessDetailsTabState from 'processes/process-details/ProcessDetailsTabState';
import ProcessDetailsTabView from 'processes/process-details/ProcessDetailsTabView';
import ProcessFlowchartTabState from 'processes/process-details/ProcessFlowchartTabState';
import ProcessFlowchartTabView from 'processes/process-details/ProcessFlowchartTabView';
import ProcessTaskDetails from 'processes/process-task-details/ProcessTaskDetails';
import ProcessTaskDetailsOutputsTabState from 'processes/process-task-details/ProcessTaskDetailsOutputsTabState';
import ProcessTaskDetailsOutputsTabView from 'processes/process-task-details/ProcessTaskDetailsOutputsTabView';
import ProcessTaskDetailsTabState from 'processes/process-task-details/ProcessTaskDetailsTabState';
import ProcessTaskFlowchartTabState from 'processes/process-task-details/ProcessTaskFlowchartTabState';
import ProcessTaskFlowchartTabView from 'processes/process-task-details/ProcessTaskFlowchartTabView';
import RequestV2DefinitionTabState from 'processes/requests_v2/request-v2-definition/RequestV2DefinitionTabState';
import RequestV2DefinitionTabView from 'processes/requests_v2/request-v2-definition/RequestV2DefinitionTabView';
import RequestV2SubmissionDocumentTabState from 'processes/requests_v2/request-v2-submission/RequestV2SubmissionDocumentTabState';
import RequestV2SubmissionDocumentTabView from 'processes/requests_v2/request-v2-submission/RequestV2SubmissionDocumentTabView';
import RequestV2SubmissionProgressAndOutputsTabState from 'processes/requests_v2/request-v2-submission/RequestV2SubmissionProgressAndOutputsTabState';
import RequestV2SubmissionProgressAndOutputsTabView from 'processes/requests_v2/request-v2-submission/RequestV2SubmissionProgressAndOutputsTabView';
import ChildEntityTabState from 'registry/child-entities/ChildEntityTabState';
import ChildEntityTabView from 'registry/child-entities/ChildEntityTabView';
import EntityMapTabState from 'registry/entity-map/tab/EntityMapTabState';
import EntityMapTabView from 'registry/entity-map/tab/EntityMapTabView';
import RelevantItemsTabState from 'relevant-items/RelevantItemsTabState';
import RelevantItemsTabView from 'relevant-items/RelevantItemsTabView';
import StorableRelevantItemsTabState from 'relevant-items/storables/StorableRelevantItemsTabState';
import StorableRelevantItemsView from 'relevant-items/storables/StorableRelevantItemsView';
import RequestDetails from 'requests/request-view/RequestDetails';
import RequestDetailsTabState from 'requests/request-view/RequestDetailsTabState';
import RequestResponse from 'requests/request-view/RequestResponse';
import RequestResponseTabState from 'requests/request-view/RequestResponseTabState';
import EntityResultsTabState from 'results/linked-results/EntityResultsTabState';
import EntityResultsView from 'results/linked-results/EntityResultsView';
import LinkedResultsTabState from 'results/linked-results/LinkedResultsTabState';
import LinkedResultsView from 'results/linked-results/LinkedResultsView';
import StorableResultsTabState from 'results/linked-results/StorableResultsTabState';
import StorableResultsView from 'results/linked-results/StorableResultsView';
import ResultStatusTabState from 'results/validation/status-tab/ResultStatusTabState';
import {ResultStatusTabView} from 'results/validation/status-tab/ResultStatusTabView';
import RunDetailsTabState from 'runs/editor/RunDetailsTabState';
import RunDetailsTabView from 'runs/editor/RunDetailsTabView';
import RunResultsTabState from 'runs/editor/RunResultsTabState';
import RunResultsTabView from 'runs/editor/RunResultsTabView';
import ContainerTestOrdersTabState from 'sample-tests/ui/test-orders/ContainerTestOrdersTabState';
import ContainerTestOrdersView from 'sample-tests/ui/test-orders/ContainerTestOrdersView';
import EntityTestOrdersTabState from 'sample-tests/ui/test-orders/EntityTestOrdersTabState';
import EntityTestOrdersView from 'sample-tests/ui/test-orders/EntityTestOrdersView';
import ContainerTabState from 'samples/containers/ContainerTabState';
import ContainerViewContainer from 'samples/containers/ContainerViewContainer';
import BoxTabState from 'samples/grids/BoxTabState';
import GridContentsTab from 'samples/grids/GridContentsTab';
import GridContentsTabState from 'samples/grids/GridContentsTabState';
import GridMetadataTab from 'samples/grids/GridMetadataTab';
import GridMetadataTabState from 'samples/grids/GridMetadataTabState';
import GridTab from 'samples/grids/GridTab';
import PlateTabState from 'samples/grids/PlateTabState';
import LocationTabState from 'samples/locations/LocationTabState';
import LocationViewContainer from 'samples/locations/LocationViewContainer';
import StorageTabState from 'samples/StorageTabState';
import StorageTabView from 'samples/StorageTabView';
import QueryingStudyProcedureRunFlowchart from 'studies/typed/shared/flowcharts/study-visualization/StudyProcedureRunFlowchart';
import StudyDesignView from 'studies/workspace/design/StudyDesignView';
import StudyDesignTabState from 'studies/workspace/tabs/StudyDesignTabState';
import StudyListingTabState from 'studies/workspace/tabs/StudyListingTabState';
import StudyListingTabView from 'studies/workspace/tabs/StudyListingTabView';
import StudyOverviewTabState from 'studies/workspace/tabs/StudyOverviewTabState';
import StudyOverviewTabView from 'studies/workspace/tabs/StudyOverviewTabView';
import VisualizationState from 'studies/workspace/tabs/VisualizationState';

const TABSTATE_TO_VIEW: Array<BufferViewMetadata> = [
  {
    tabState: SequenceAlignmentTabState,
    /*
     * SequenceAlignmentTabView contains listeners on a number of SequenceAlignmentRowState child Models.
     * Additionally, its presentation is managed by Backbone - ultimately, it is rendered by a bunch of
     * SliceView, most of which are still rendered with Backbone and d3. In order for SequenceAlignmentTabView
     * to be migrated to React, we would have to invent a pure React solution for sequence maps broadly, in
     * addition to migrating the various Models to Redux.
     */
    tabViewMetadata: {TabView: SequenceAlignmentTabView, tabViewType: BufferViewType.BACKBONE},
    /*
     * SequenceAlignmentTabOptionsView contains listeners on SequenceAlignmentTabState, the Sequence model
     * that the alignment belongs to, and the AlignmentAnalysis model itself. However, its actual presentation
     * is rendered with React, so it could be converted if these three Backbone Models were migrated to Redux.
     */
    optionsViewMetadata: {
      OptionsView: SequenceAlignmentTabOptionsView,
      optionsViewType: BufferViewType.BACKBONE,
    },
  },
  {
    tabState: AlphaFoldTabState,
    tabViewMetadata: {
      TabView: AlphaFoldTabView,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: AlphaFoldTabState): AlphaFoldTabContentProps => {
        const initialView = tabState.get('initialView');
        if (initialView != null) {
          return {
            aaSequenceId: tabState.protein().id,
            initialView,
            clearInitialView: () => tabState.unset('initialView'),
          };
        }
        return {aaSequenceId: tabState.protein().id};
      },
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
    optionsViewMetadata: {
      OptionsView: AlphaFoldOptionsView,
      optionsViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: AlphaFoldTabState): AlphaFoldOptionsProps => ({
        proteinId: tabState.protein().id,
      }),
    },
    dataTestAttrs: {component: 'AaSequenceStructureTab', element: 'root'},
  },
  {
    tabState: AaSequenceAnnotationsTableTabState,
    tabViewMetadata: {
      TabView: AaSequenceExpandedAnnotationTab,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: AaSequenceAnnotationsTableTabState) => ({
        aaSequenceId: tabState.protein().id,
      }),
    },
  },
  {
    tabState: AnalysisTabState,
    /**
     * AnalysisUISkeleton props can easily be calculated from AnalysisTabState, but its approach to GraphQL,
     * combined with quirks of our Apollo utilities and hooks, means that it does not reliably receive the
     * correct state from the cache when switching between two analyses.
     * TODO(BNCH-151836): When `renderCachedDataWhenRefetching` no longer returns stale data for one render
     * cycle, this tab can be converted to React.
     */
    tabViewMetadata: {
      TabView: AnalysisTabView,
      tabViewType: BufferViewType.BACKBONE,
    },
  },
  /**
   * AnalysisTemplateTabView checks a config flag, which must be accessed at runtime. When that flag is fully rolled
   * out, this tab can be converted to React.
   */
  {
    tabState: AnalysisTemplateTabState,
    tabViewMetadata: {TabView: AnalysisTemplateTabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: AnalyticsDashboardTabState,
    tabViewMetadata: {
      TabView: AnalyticsDashboardDetails,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: AnalyticsDashboardTabState) => ({
        analyticsDashboardId: tabState.get('analyticsDashboardId'),
        viewStateId: tabState.get('viewStateId'),
        ...DEFAULT_DASHBOARD_DETAILS_CONTEXT,
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: AnnotationsTableTabState,
    /*
     * While AnnotationsTableTabView is ultimately rendered in React, it contains listeners on the Backbone
     * Sequence Model that owns the annotations. The Sequence Model must be migrated to Redux before this
     * view can be migrated to React.
     */
    tabViewMetadata: {TabView: AnnotationsTableTabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: AntibodyMapTabState,
    tabViewMetadata: {
      TabView: AntibodyMap,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: AntibodyMapTabState) => ({
        antibodyId: tabState.get('antibodyId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
    optionsViewMetadata: {
      OptionsView: AntibodyMapTabOptions,
      optionsViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: AntibodyMapTabState) => ({
        antibodyId: tabState.get('antibodyId'),
        isEditable: tabState.get('isEditable'),
      }),
      additionalOptionsClassNames: 'reset',
    },
  },
  {
    tabState: ProteinEntityStructureTabState,
    tabViewMetadata: {
      TabView: ProteinEntityStructureTabView,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: ProteinEntityStructureTabState): ProteinEntityStructureTabViewProps => ({
        proteinId: tabState.get('proteinId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
    dataTestAttrs: {component: 'ProteinEntityStructureTab', element: 'root'},
  },
  {
    tabState: AssemblyHistoryTabState,
    /*
     * While AssemblyHistoryTabView is ultimately rendered in React, it contains listeners on the Backbone
     * polymer Model that owns the assembly history. The polymer Model must be migrated to Redux before this
     * view can be migrated to React.
     */
    tabViewMetadata: {TabView: AssemblyHistoryTabView, tabViewType: BufferViewType.BACKBONE},
    dataTestAttrs: {component: 'AssemblyHistoryTab', element: 'root'},
  },
  {
    tabState: BenchlingAppTabState,
    tabViewMetadata: {
      TabView: BenchlingAppDetails,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: BenchlingAppTabState) => ({
        benchlingAppId: tabState.get('benchlingAppId'),
      }),
      additionalTabClassNames: classNames(
        'buffer-tab--no-padding',
        css(BENCHLING_APP_BUFFER_TAB_STYLES.background)
      ),
    },
  },
  {
    tabState: TranslationBiochemicalPropertiesTabState,
    /*
     * While BiochemicalPropertiesTabView is ultimately rendered in React, it contains listeners on the
     * Backbone polymer Model whose biochemical properties are being inspected. The polymer Model must be
     * migrated to Redux before this view can be migrated to React.
     */
    tabViewMetadata: {TabView: BiochemicalPropertiesTabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: AaSequenceBiochemicalPropertiesTabState,
    /*
     * While BiochemicalPropertiesTabView is ultimately rendered in React, it contains listeners on the
     * Backbone polymer Model whose biochemical properties are being inspected. The polymer Model must be
     * migrated to Redux before this view can be migrated to React.
     */
    tabViewMetadata: {TabView: BiochemicalPropertiesTabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: BlobLinkTab.tabState,
    /*
     * While BlobLinkTabView is ultimately rendered in React, it contains listeners on the TabState, so it
     * cannot be migrated to React until we invent a replacement for Backbone TabStates.
     */
    tabViewMetadata: {TabView: BlobLinkTab.tabView, tabViewType: BufferViewType.BACKBONE},
    /*
     * While BlobLinkOptionsView is ultimately rendered in React, it contains listeners on the TabState, so it
     * cannot be migrated to React until we invent a replacement for Backbone TabStates.
     */
    optionsViewMetadata: {OptionsView: BlobLinkTab.optionsView, optionsViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: BoxTabState,
    tabViewMetadata: {
      TabView: GridTab,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: BoxTabState) => ({
        gridId: tabState.get('gridId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: BulkAssemblyConstructsTabState,
    tabViewMetadata: {
      TabView: BulkAssemblyConstructsTab,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: BulkAssemblyConstructsTabState) => ({
        assemblyId: tabState.bulkAssemblyModel().getBulkAssembly().id,
      }),
    },
  },
  {
    tabState: BulkAssemblyOverviewTabState,
    /*
     * BulkAssemblyOverviewTabView could be converted directly to React with a small refactor to move logic
     * that only interacts with Flux out of Backbone, either by inlining it if called in only one place, or by
     * creating a pure function called by both Backbone and React. Specifically, that means
     * BulkAssemblyOverviewTabView.onSetFolder and BaseFolderItem.triggerUpdateNameAction.
     */
    tabViewMetadata: {TabView: BulkAssemblyOverviewTabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: ChildEntityTabState,
    /*
     * While ChildEntityTabView is ultimately rendered in React, it contains listeners on the parent model, so
     * it cannot be migrated to React until we invent a replacement for Backbone Models broadly.
     */
    tabViewMetadata: {TabView: ChildEntityTabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: CircularMapTabState,
    /*
     * Most of CircularMapTabView is still mostly rendered imperatively with d3, and that rendering is
     * delegated to a large number of child RingViews. There is a shim for rendering each RingView in React,
     * but the coordination would need to be migrated as well before Backbone could be removed entirely from
     * the circular map. Additionally, the circular map contains mousewheel events managed by d3, which must
     * be migrated to native listeners.
     */
    tabViewMetadata: {TabView: CircularMapTabView, tabViewType: BufferViewType.BACKBONE},
    /*
     * While CircularMapTabOptionsView is ultimately rendered with React, it contains a number of listeners
     * on the Backbone Sequence Model, as well as a sizeable amount of business logic.
     */
    optionsViewMetadata: {OptionsView: CircularMapTabOptionsView, optionsViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: CodonOptimizationTabState,
    /*
     * While CodonOptimizationTabView is ultimately rendered with React, it contains listeners on the Backbone
     * Sequence Model. That Model must be migrated to Redux before this tab can be converted to React.
     */
    tabViewMetadata: {TabView: CodonOptimizationTabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: ComplexPolymerMapTabState,
    tabViewMetadata: {
      TabView: ComplexPolymerMap,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: ComplexPolymerMapTabState) => ({
        complexPolymerId: tabState.get('complexPolymerId'),
        isEditable: tabState.get('isEditable'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: ComplexPolymerComponentsTabState,
    tabViewMetadata: {
      TabView: ComplexPolymerComponentsTableTab,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: ComplexPolymerComponentsTabState) => ({
        complexPolymerId: tabState.get('complexPolymerId'),
        isEditable: tabState.get('isEditable'),
      }),
    },
  },
  {
    tabState: ConnectionDataTabState,
    /**
     * ConnectionDataTab can probably be rendered with React. However, it is impossible to verify whether it
     * handles switching between two active ConnectionDataTab properly because the setup documentation for
     * Connections do not work.
     */
    tabViewMetadata: {TabView: ConnectionDataTabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: ConnectionDetailsTabState,
    /**
     * ConnectionDetailsTab can probably be rendered with React. However, it is impossible to verify whether it
     * handles switching between two active ConnectionDetailsTab properly because the setup documentation for
     * Connections do not work.
     */
    tabViewMetadata: {TabView: ConnectionDetailsTabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: ConnectionRunsTabState,
    /**
     * ConnectionRunsTab can probably be rendered with React. However, it is impossible to verify whether it
     * handles switching between two active ConnectionRunsTab properly because the setup documentation for
     * Connections do not work.
     */
    tabViewMetadata: {TabView: ConnectionRunsTabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: ContainerTabState,
    tabViewMetadata: {
      TabView: ContainerViewContainer,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: ContainerTabState) => ({
        containerId: tabState.get('containerId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: CRISPRAnalysisTabState,
    /*
     * While CRISPRAnalysisTabView is ultimately rendered in React, it contains listeners on the Backbone
     * Sequence Model that owns the CRISPR analysis. Additionally, it contains extensive business logic. In
     * order for CRISPRAnalysisTabView to be migrated to React, this business logic would need to be either
     * inlined or made purely functional, and the Sequence Model would need to be migrated to Redux.
     */
    tabViewMetadata: {TabView: CRISPRAnalysisTabView, tabViewType: BufferViewType.BACKBONE},
    /*
     * While CRISPRAnalysisOptionsView is ultimately rendered in React, it contains listeners on the Backbone
     * Sequence Model that owns the CRISPR analysis. In order for CRISPRAnalysisOptionsView to be migrated to
     * React, the Sequence Model would need to be migrated to Redux.
     */
    optionsViewMetadata: {OptionsView: CRISPRAnalysisOptionsView, optionsViewType: BufferViewType.BACKBONE},
    dataTestAttrs: {component: 'DesignCRISPRTab', element: 'root'},
  },
  {
    tabState: CRISPRAssemblyTabState,
    tabViewMetadata: {
      TabView: ConnectedCRISPRAssemblyTab,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: CRISPRAssemblyTabState) => ({
        assemblyCid: tabState.assemblyCid(),
        sequenceName: tabState.sequenceName(),
      }),
      additionalTabClassNames: 'buffer-tab-reset',
    },
    optionsViewMetadata: {
      OptionsView: CRISPRAssemblyOptions,
      optionsViewType: BufferViewType.REACT,
      propsFromTabState: () => ({}),
      additionalOptionsClassNames: 'navbar',
    },
  },
  {
    tabState: DatasetTabState,
    tabViewMetadata: {
      TabView: DatasetView,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: DatasetTabState) => ({
        id: tabState.get('id'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: DigestTabState,
    /*
     * DigestView sets listeners on the Sequence Model being digested, as well as the Digest Model itself.
     * Additionally, it sets DOM event handlers, and renders the DOM itself via Jade style template. Its
     * rendering and event handling must be converted to React and the Models ported to Redux before this tab
     * can be rendered in React.
     */
    tabViewMetadata: {TabView: DigestView.TabView, tabViewType: BufferViewType.BACKBONE},
    /*
     * DigestOptions sets listeners on the Sequence Model being digested, as well as the Digest Model itself.
     * Additionally, it sets DOM event handlers, and renders the DOM itself via Jade style template. Its
     * rendering and event handling must be converted to React and the Models ported to Redux before this tab
     * can be rendered in React.
     */
    optionsViewMetadata: {OptionsView: DigestView.OptionsView, optionsViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: EditPrimerTabState,
    /*
     * EditPrimerView sets listeners on the Sequence Model that primers are being designed for, as well as the
     * Primer Models themselves. Additionally, it sets DOM event handlers, and renders most of the DOM itself
     * via Jade style template. Its rendering and event handling must be converted to React and the Models
     * ported to Redux before this tab can be rendered in React.
     */
    tabViewMetadata: {TabView: EditPrimerView.TabView, tabViewType: BufferViewType.BACKBONE},
    /*
     * EditPrimerOptions sets listeners on the Sequence Model that primers are being designed for, as well as
     * the Primer Models themselves. Additionally, it sets DOM event handlers, and renders most of the DOM
     * itself via Jade style template. Its rendering and event handling must be converted to React and the
     * Models ported to Redux before this tab can be rendered in React.
     */
    optionsViewMetadata: {OptionsView: EditPrimerView.OptionsView, optionsViewType: BufferViewType.BACKBONE},
    dataTestAttrs: {component: 'EditPrimerTab', element: 'root'},
  },
  {
    tabState: EmbeddedAnalyticsDashboardTabState,
    tabViewMetadata: {
      TabView: EmbeddedAnalyticsDashboardView,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: EmbeddedAnalyticsDashboardTabState) => ({
        analyticsDashboardLink: tabState.get('analyticsDashboardLink'),
        itemId: tabState.get('itemId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: EmptyEntryTabState,
    tabViewMetadata: {
      TabView: EmptyEntryTab,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: () => ({}),
    },
  },
  {
    tabState: EntityResultsTabState,
    tabViewMetadata: {
      TabView: EntityResultsView,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: EntityResultsTabState) => ({
        entityId: tabState.get('model').id,
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
    dataTestAttrs: {component: 'EntityResultsTab', element: 'root'},
  },
  {
    tabState: EntityMapTabState,
    /**
     * EntityMapTabView could be rendered in React, but EntityMapMachineProvider does not update its data when switching
     * between two entities that each have the EntityMapTabView active. When EntityMapMachineProvider handles its
     * `entityId` changing, EntityMapTab can be trivially converted to React.
     */
    tabViewMetadata: {
      TabView: EntityMapTabView,
      tabViewType: BufferViewType.BACKBONE,
    },
  },
  {
    tabState: EntryProtocolTabState,
    /*
     * EntryProtocolTabView renders React that both listens to and updates the underlying Backbone Protocol
     * Model. Additionally, it uses JQuery to imperatively scroll to a specific place in the page when the
     * Protocol loads. The Protocol Model must be ported to Redux and the scroll logic must be replicated with
     * a React hook or similar before this tab can be rendered purely in React.
     */
    tabViewMetadata: {TabView: EntryProtocolView.TabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: ExternalEntryProtocolTabState,
    /*
     * ExternalEntryProtocolTabView renders React, but listens to its ExternalFile Model. That Model must be
     * ported to Redux before this tab can be rendered purely in React.
     */
    tabViewMetadata: {TabView: ExternalEntryProtocolTabView, tabViewType: BufferViewType.BACKBONE},
    /*
     * ExternalEntryProtocolOptionsView renders React, but listens to its ExternalFile Model. That Model must
     * be ported to Redux before this view can be rendered purely in React.
     */
    optionsViewMetadata: {
      OptionsView: ExternalEntryProtocolOptionsView,
      optionsViewType: BufferViewType.BACKBONE,
    },
  },
  {
    tabState: FileTabState,
    tabViewMetadata: {
      TabView: FileView,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: FileTabState) => ({
        id: tabState.get('id'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: FindPrimersTabState,
    /*
     * Although it is ultimately rendered with React, this view puts dozens of listeners on the Sequence Model
     * that the tab is designing primers for. Additionally, it contains extensive business logic operating
     * over Backbone SequencePrimer Models and the SequencePrimers Collection. In order to migrate this view,
     * the Sequence Model must be migrated to Redux and the business logic must be made into pure functions.
     */
    tabViewMetadata: {TabView: FindPrimersView, tabViewType: BufferViewType.BACKBONE},
    /*
     * Although it is ultimately rendered with React, this view puts a handful of listeners on the Sequence
     * Model that the tab is designing primers for. Additionally, it contains business logic operating over
     * Backbone SequencePrimer Models and the SequencePrimers Collection. In order to migrate this view,
     * the Sequence Model must be migrated to Redux and the business logic must be made into a pure function.
     */
    optionsViewMetadata: {OptionsView: FindPrimersOptionsView, optionsViewType: BufferViewType.BACKBONE},
    dataTestAttrs: {component: 'FindPrimersTab', element: 'root'},
  },
  {
    tabState: FormDefinitionTabState,
    tabViewMetadata: {
      TabView: EditFormDefinitionPage,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: FormDefinitionTabState) => ({
        formDefinitionId: tabState.get('formDefinitionId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: AgentFoundryTabState,
    tabViewMetadata: {
      TabView: AgentFoundryPage,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: AgentFoundryTabState) => ({
        agentFoundryId: tabState.get('agentFoundryId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: PlateDesignTemplateTabState,
    tabViewMetadata: {
      TabView: PlateDesignTemplatePage,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: PlateDesignTemplateTabState) => ({
        plateDesignTemplateId: tabState.get('plateDesignTemplateId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: GenerativeModelsTabState,
    /**
     * GenerativeModelsView props can easily be calculated from GenerativeModelsTabState, but its possibly-unnecessary
     * memoization of GraphQL results means that it does not reliably receive the correct data from
     * `useGetGenerativeModelRuns` when switching between two entities that each have a GenerativeModelsTab active. This
     * hook must be fixed before this tab can be converted to React.
     */
    tabViewMetadata: {
      TabView: GenerativeModelsTabView,
      tabViewType: BufferViewType.BACKBONE,
    },
  },
  {
    tabState: GibsonTabState,
    /*
     * GibsonTabView is-a PrimerAssemblyTabView. This renders React under the hood, but listens to both the
     * TabState Model and the associated Sequence Model. Both must be migrated to Redux before this tab can
     * be rendered directly with React.
     */
    tabViewMetadata: {TabView: GibsonTabView.TabView, tabViewType: BufferViewType.BACKBONE},
    /*
     * GibsonTabView is-a PrimerAssemblyOptionsView. This renders with an old Jade style template. It listens
     * to both the TabState Model and the associated Sequence Model. Additionally, it has DOM event listeners.
     */
    optionsViewMetadata: {OptionsView: GibsonTabView.OptionsView, optionsViewType: BufferViewType.BACKBONE},
    dataTestAttrs: {component: 'GibsonAssemblyTab', element: 'root'},
  },
  {
    tabState: GoldenGateTabState,
    /*
     * GoldenGateTabView is-a PrimerAssemblyTabView. This renders React under the hood, but listens to both
     * the TabState Model and the associated Sequence Model. Both must be migrated to Redux before this tab
     * can be rendered directly with React.
     */
    tabViewMetadata: {TabView: GoldenGateTabView.TabView, tabViewType: BufferViewType.BACKBONE},
    /*
     * GoldenGateOptionsView is-a PrimerAssemblyOptionsView. This renders with an old Jade style template. It
     * listens to both the TabState Model and the associated Sequence Model. Additionally, it has DOM event
     * listeners.
     */
    optionsViewMetadata: {
      OptionsView: GoldenGateTabView.OptionsView,
      optionsViewType: BufferViewType.BACKBONE,
    },
  },
  {
    tabState: GridContentsTabState,
    tabViewMetadata: {
      TabView: GridContentsTab,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: GridContentsTabState) => ({
        gridId: tabState.get('gridId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: GridMetadataTabState,
    tabViewMetadata: {
      TabView: GridMetadataTab,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: GridMetadataTabState) => ({
        gridId: tabState.get('gridId'),
      }),
    },
  },
  {
    tabState: HRTemplateTabState,
    /*
     * HRTemplateTabView renders React, but has a handful of listeners on the Sequence Model that the HR
     * Template is being designed against. The Sequence Model must be ported to Redux before this tab can be
     * rendered in pure React.
     */
    tabViewMetadata: {TabView: HRTemplateTabView.TabView, tabViewType: BufferViewType.BACKBONE},
    /*
     * HRTemplateOptionsView renders React, but has a listener on the Sequence Model that the HR Template is
     * being designed against. The Sequence Model must be ported to Redux before this options view can be
     * rendered in pure React.
     */
    optionsViewMetadata: {
      OptionsView: HRTemplateTabView.OptionsView,
      optionsViewType: BufferViewType.BACKBONE,
    },
  },
  {
    tabState: JellyfishTabState,
    tabViewMetadata: {
      TabView: JellyfishView,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: JellyfishTabState) => ({id: tabState.get('id')}),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: LocationTabState,
    tabViewMetadata: {
      TabView: LocationViewContainer,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: LocationTabState) => ({locationId: tabState.get('id')}),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: MetadataTabState,
    /*
     * MetadataTabView renders React, but also contains a handful of listeners on the Backbone Model for which
     * it displays metadata. ALL such Models must be ported to Redux before this view can be rendered with
     * pure React.
     */
    tabViewMetadata: {TabView: MetadataTabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: MiniMapTabState,
    /*
     * Most of MiniMapTabView is still mostly rendered imperatively with d3, and that rendering is delegated
     * to a large number of child SliceViews. There is a shim for rendering each SliceView in React, but the
     * coordination would need to be migrated as well before Backbone could be removed entirely from the
     * minimap. Additionally, the minimap handles scroll events managed by jQuery, which must be migrated to
     * native listeners.
     */
    tabViewMetadata: {TabView: MiniMapTabView, tabViewType: BufferViewType.BACKBONE},
    /*
     * While MiniMapTabOptionsView is ultimately rendered with React, it contains a number of listeners on
     * the TabState Model, the Sequence Model, and the MapState Model, which stores state like zoom and pan.
     * Additionally, it handles VisibilityOptions, which is a Backbone Collection. All of these Backbone
     * objects would need to be ported to React before this options view could be rendered with pure React.
     */
    optionsViewMetadata: {OptionsView: MiniMapTabOptionsView, optionsViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: MoleculeChemicalStructureTabState,
    tabViewMetadata: {
      TabView: MoleculeChemicalStructureTab,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: MoleculeChemicalStructureTabState) => ({
        moleculeId: tabState.get('moleculeId'),
        // N.B.: This is a Backbone Molecule Model instance. We should move away from this pattern and toward
        // storing global entity state in Redux.
        molecule: tabState.get('molecule'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: MoleculePhysicochemicalPropertiesTabState,
    tabViewMetadata: {
      TabView: MoleculePhysicochemicalPropertiesTab,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: MoleculePhysicochemicalPropertiesTabState) => ({
        moleculeId: tabState.get('moleculeId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: MoleculePredictedPropertiesTabState,
    tabViewMetadata: {
      TabView: MoleculePredictedPropertiesTab,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: MoleculePredictedPropertiesTabState) => ({
        moleculeId: tabState.get('moleculeId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: NewEntryProtocolTabState,
    /*
     * NewEntryProtocolTabView renders React and does not have listeners; however, it updates the state of the
     * TabState Model as part of handling user actions. Before this view can be rendered with pure React, we
     * must migrate TabStates to Redux.
     */
    tabViewMetadata: {TabView: NewEntryProtocolTabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: NoteLinkedEmbeddingTab.tabState,
    /*
     * NoteLinkedEmbeddingTabView renders React, but listens to the TabState Model to know when to rerender.
     * Before this view can be rendered with pure React, we must migrate TabStates to Redux.
     */
    tabViewMetadata: {TabView: NoteLinkedEmbeddingTab.tabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: NoteLinkedFileTab.tabState,
    /*
     * NoteLinkedFileTabView renders React, but it both listens to the TabState Model to know when to rerender
     * and updates the state of the TabState Model as part of handling user actions. Before this view can be
     * rendered with pure React, we must migrate TabStates to Redux.
     */
    tabViewMetadata: {TabView: NoteLinkedFileTab.tabView, tabViewType: BufferViewType.BACKBONE},
    /*
     * NoteLinkedFileOptionsView renders React, but it both listens to the TabState Model to know when to
     * rerender and updates the state of the TabState Model as part of handling user actions. Before this view
     * can be rendered with pure React, we must migrate TabStates to Redux.
     */
    optionsViewMetadata: {
      OptionsView: NoteLinkedFileTab.optionsView,
      optionsViewType: BufferViewType.BACKBONE,
    },
  },
  {
    tabState: NoteTabState,
    /*
     * NoteTabView renders React, but sets listeners on the TabState Model. Additionally, it uses jQuery to
     * imperatively set scroll state on note load. In order for this tab to be rendered purely in React,
     * TabStates must be ported to Redux and the scroll handling must be replicated with a React hook or
     * similar functionality.
     */
    tabViewMetadata: {TabView: NoteTabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: NucleotidesTabState,
    /*
     * NucleotidesTabView renders React, but sets listeners on the Sequence Model. In order for this tab to
     * be rendered purely in React, Sequence must be ported to Redux.
     */
    tabViewMetadata: {TabView: NucleotidesTabView, tabViewType: BufferViewType.BACKBONE},
    dataTestAttrs: {component: 'NucleotidesTab', element: 'root'},
  },
  {
    tabState: OligoLinearMapTabState,
    /*
     * Most of SequenceLinearMapTabView is still mostly rendered imperatively with d3, and that rendering is
     * delegated to a large number of child SliceViews. There is a shim for rendering each SliceView in React,
     * but the coordination would need to be migrated as well before Backbone could be removed entirely from
     * the sequence map. Additionally, the sequence map handles scroll events managed by InfiniteScroller,
     * which must be migrated to native listeners.
     */
    tabViewMetadata: {TabView: SequenceLinearMapTabView, tabViewType: BufferViewType.BACKBONE},
    /*
     * While LinearMapTabOptionsView is ultimately rendered with React, it contains a number of listeners on
     * the TabState Model, the Sequence Model, and the MapState Model, which stores state like zoom and pan.
     * Additionally, it handles VisibilityOptions, which is a Backbone Collection. All of these Backbone
     * objects would need to be ported to React before this options view could be rendered with pure React.
     */
    optionsViewMetadata: {OptionsView: LinearMapTabOptionsView, optionsViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: PlateTabState,
    tabViewMetadata: {
      TabView: GridTab,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: PlateTabState) => ({gridId: tabState.get('gridId')}),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: Primer3TabState,
    /*
     * Primer3TabView renders a Jade-style template. It sets listeners on DOM events, as well as on the
     * Sequence Model, some of its child Models and Collections, and on Preferences. All of these models must
     * be migrated to Redux and the template must be migrated to React before this tab can be rendered with
     * React.
     */
    tabViewMetadata: {TabView: Primer3View.TabView, tabViewType: BufferViewType.BACKBONE},
    /*
     * Primer3OptionsView renders a Jade-style template. It sets listeners on DOM events, as well as on the
     * Sequence Model, some of its child Models and Collections, and on Preferences. All of these models must
     * be migrated to Redux and the template must be migrated to React before this options view can be
     * rendered with React.
     */
    optionsViewMetadata: {OptionsView: Primer3View.OptionsView, optionsViewType: BufferViewType.BACKBONE},
    dataTestAttrs: {component: 'Primer3ResultsTab', element: 'root'},
  },
  {
    tabState: PrimerWizardTabState,
    /*
     * PrimerWizardTabView renders React, but listens to the Sequence Model for updates on its Selection child
     * Model. Sequence selection state must be migrated to Redux before this tab can be rendered with pure
     * React.
     */
    tabViewMetadata: {TabView: PrimerWizardTab.TabView, tabViewType: BufferViewType.BACKBONE},
    /*
     * PrimerWizardsOptionsView renders React, but it performs updates on the Sequence Model and depends on
     * business logic in the TabState Model. In order for this options view to be rendered with pure React,
     * the business logic must be factored into a pure function and the Sequence Model must be ported to
     * Redux.
     */
    optionsViewMetadata: {OptionsView: PrimerWizardTab.OptionsView, optionsViewType: BufferViewType.BACKBONE},
    dataTestAttrs: {component: 'PrimerWizardTab', element: 'root'},
  },
  {
    tabState: ProcedureTabState,
    /**
     * ProcedurePage props can easily be calculated from ProcedureTabState, but its approach to GraphQL,
     * combined with quirks of our Apollo utilities and hooks, means that it does not reliably receive the
     * correct state from the cache when switching between two entities that each have a ProcedurePage active.
     * TODO(BNCH-151836): When `renderCachedDataWhenRefetching` no longer returns stale data for one render
     * cycle, this tab can be converted to React.
     */
    tabViewMetadata: {
      TabView: ProcedureTabView,
      tabViewType: BufferViewType.BACKBONE,
    },
  },
  {
    tabState: ProcessDetailsTabState,
    /*
     * ProcessDetailsTabView renders React, but listens to the TabState Model. It only listens to its fileId
     * attribute, which may only change on a full tab clear/rerender anyways - so this listener may not be
     * necessary.
     */
    tabViewMetadata: {TabView: ProcessDetailsTabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: ProcessFlowchartTabState,
    /*
     * ProcessFlowchartTabView renders React, but listens and updates the TabState Model. It only listens to
     * its fileId attribute, which may only change on a full tab clear/rerender anyways - so this listener
     * may not be necessary. The setter is a different story - to replicate that functionality without
     * Backbone, we must port TabStates to Redux.
     */
    tabViewMetadata: {TabView: ProcessFlowchartTabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: ProcessTaskDetailsOutputsTabState,
    tabViewMetadata: {
      TabView: ProcessTaskDetailsOutputsTabView,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: ProcessTaskDetailsOutputsTabState) => ({
        processTaskId: tabState.get('processTaskId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: ProcessTaskDetailsTabState,
    tabViewMetadata: {
      TabView: ProcessTaskDetails,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: ProcessTaskDetailsTabState) => ({
        processTaskId: tabState.get('processTaskId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: ProcessTaskFlowchartTabState,
    tabViewMetadata: {
      TabView: ProcessTaskFlowchartTabView,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: ProcessTaskFlowchartTabState) => ({
        missingFlowchartMessage: tabState.get('missingFlowchartMessage'),
        rootTaskId: tabState.get('rootTaskId'),
        taskId: tabState.get('procedureMethodTaskId') ?? tabState.get('taskId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: AaSequenceAlignmentTabState,
    /*
     * ProteinAlignmentTabView contains listeners on a number of ProteinAlignmentRow child Models.
     * Additionally, its presentation is managed by Backbone - ultimately, it is rendered by a bunch of
     * SliceView, most of which are still rendered with Backbone and d3. In order for ProteinAlignmentTabView
     * to be migrated to React, we would have to invent a pure React solution for sequence maps broadly, in
     * addition to migrating the various Models to Redux.
     */
    tabViewMetadata: {TabView: AaSequenceAlignmentTabView, tabViewType: BufferViewType.BACKBONE},
    /*
     * ProteinAlignmentTabOptionsView contains listeners on the Sequence Model's Selection child. While it
     * ultimately renders a React component, its export feature depends on SliceView logic that requires
     * pulling data from the TabState and instantiating a bunch of SliceView.
     */
    optionsViewMetadata: {
      OptionsView: AaSequenceAlignmentTabOptionsView,
      optionsViewType: BufferViewType.BACKBONE,
    },
  },
  {
    tabState: AaSequenceComplexPreviewTab.tabState,
    tabViewMetadata: {
      TabView: AaSequenceComplexPreview,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: AaSequenceComplexPreviewTab.tabState) => ({
        proteinIds: tabState.get('proteinIds'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: AaSequenceComplexPropertiesTab.tabState,
    tabViewMetadata: {
      TabView: AaSequenceComplexProperties,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: AaSequenceComplexPropertiesTab.tabState) => ({
        proteinIds: tabState.get('proteinIds'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: AaSequenceMapTabState,
    /*
     * AaSequenceMapView delegates its rendering to a large number of child SliceViews. These SliceViews
     * ultimately render React, but the coordination would need to be migrated as well before Backbone could
     * be removed entirely from the AA sequence map. Additionally, the AA sequence map has a number of
     * listeners on the TabState Model, the Sequence Model, and the MapState Model, all of which must be
     * ported to Redux before the AA sequence map can be rendered with pure React.
     */
    tabViewMetadata: {TabView: AaSequenceMapView.TabView, tabViewType: BufferViewType.BACKBONE},
    /*
     * AaSequenceMapView renders with React, but contains a number of listeners on the MapState Model, as well as
     * passing functions to the React component to update the MapState. MapStates must be ported to Redux
     * before the AA sequence map options view can be rendered with pure React.
     */
    optionsViewMetadata: {
      OptionsView: AaSequenceMapView.OptionsView,
      optionsViewType: BufferViewType.BACKBONE,
    },
  },
  {
    tabState: ProtocolTabState,
    /*
     * ProtocolView renders with React, but contains a couple of listeners on the TabState Model. TabStates
     * must be ported to Redux before ProtocolView can be rendered with pure React.
     */
    tabViewMetadata: {TabView: ProtocolView.TabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: RelevantItemsTabState,
    /*
     * RelevantItemTabView renders with React, but contains a broad change listener on the TabState Model.
     * TabStates must be ported to Redux before RelevantItemsTabView can be rendered with React.
     */
    tabViewMetadata: {TabView: RelevantItemsTabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: RequestDetailsTabState,
    tabViewMetadata: {
      TabView: RequestDetails,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: RequestDetailsTabState) => ({
        requestId: tabState.get('requestId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: RequestResponseTabState,
    tabViewMetadata: {
      TabView: RequestResponse,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: RequestResponseTabState) => ({
        requestId: tabState.get('requestId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: RequestV2DefinitionTabState,
    tabViewMetadata: {
      TabView: RequestV2DefinitionTabView,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: RequestV2DefinitionTabState) => ({
        requestV2DefinitionId: tabState.get('requestV2DefinitionId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: RequestV2SubmissionDocumentTabState,
    tabViewMetadata: {
      TabView: RequestV2SubmissionDocumentTabView,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: RequestV2SubmissionDocumentTabState) => ({
        requestV2SubmissionId: tabState.get('id'),
        // N.B.: Preferably, we would not pass Backbone logic to React; when TabStates and FileStates are
        // migrated to Redux, instead of reaching up the chain to FileStates, we could simply add another tab
        onSubmissionAddProgressAndOutputsTab: (): void => {
          const requestV2SubmissionFileState = tabState.getRequestV2SubmissionFileState();
          requestV2SubmissionFileState.addProgressAndOutputsTab();
        },
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: RequestV2SubmissionProgressAndOutputsTabState,
    tabViewMetadata: {
      TabView: RequestV2SubmissionProgressAndOutputsTabView,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: RequestV2SubmissionProgressAndOutputsTabState) => ({
        requestV2SubmissionId: tabState.getRequestV2SubmissionFileState().id,
        folder: tabState.getRequestV2SubmissionFileState().model().folder(),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: ResultStatusTabState,
    /*
     * ResultStatusTabView renders with React, but contains a broad change listener on the TabState Model.
     * TabStates must be ported to Redux before ResultStatusTabView can be rendered with React.
     */
    tabViewMetadata: {TabView: ResultStatusTabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: ReviewTabState,
    /*
     * ReviewTabView renders with React, but contains a broad change listener on the TabState Model. TabStates
     * must be ported to Redux before ReviewTabView can be rendered with React.
     */
    tabViewMetadata: {TabView: ReviewTabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: ReviewHistoryTabState,
    /*
     * ReviewHistoryTabView renders React, but listens to the TabState Model. It only listens to its fileId
     * attribute, which may only change on a full tab clear/rerender anyways - so this listener may not be
     * necessary.
     */
    tabViewMetadata: {TabView: ReviewHistoryTabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: RNASequenceLinearMapTabState,
    /*
     * Most of SequenceLinearMapTabView is still mostly rendered imperatively with d3, and that rendering is
     * delegated to a large number of child SliceViews. There is a shim for rendering each SliceView in React,
     * but the coordination would need to be migrated as well before Backbone could be removed entirely from
     * the sequence map. Additionally, the sequence map handles scroll events managed by InfiniteScroller,
     * which must be migrated to native listeners.
     */
    tabViewMetadata: {TabView: SequenceLinearMapTabView, tabViewType: BufferViewType.BACKBONE},
    /*
     * While LinearMapTabOptionsView is ultimately rendered with React, it contains a number of listeners on
     * the TabState Model, the Sequence Model, and the MapState Model, which stores state like zoom and pan.
     * Additionally, it handles VisibilityOptions, which is a Backbone Collection. All of these Backbone
     * objects would need to be ported to React before this options view could be rendered with pure React.
     */
    optionsViewMetadata: {OptionsView: LinearMapTabOptionsView, optionsViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: RNASequenceMiniMapTabState,
    /*
     * Most of MiniMapTabView is still mostly rendered imperatively with d3, and that rendering is delegated
     * to a large number of child SliceViews. There is a shim for rendering each SliceView in React, but the
     * coordination would need to be migrated as well before Backbone could be removed entirely from the
     * minimap. Additionally, the minimap handles scroll events managed by jQuery, which must be migrated to
     * native listeners.
     */
    tabViewMetadata: {TabView: MiniMapTabView, tabViewType: BufferViewType.BACKBONE},
    /*
     * While MiniMapTabOptionsView is ultimately rendered with React, it contains a number of listeners on
     * the TabState Model, the Sequence Model, and the MapState Model, which stores state like zoom and pan.
     * Additionally, it handles VisibilityOptions, which is a Backbone Collection. All of these Backbone
     * objects would need to be ported to React before this options view could be rendered with pure React.
     */
    optionsViewMetadata: {OptionsView: MiniMapTabOptionsView, optionsViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: CanvasDefinitionTabState,
    tabViewMetadata: {
      TabView: CanvasDefinitionTabView,
      tabViewType: BufferViewType.BACKBONE,
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: CanvasFolderArtifactTabState,
    tabViewMetadata: {
      TabView: CanvasFolderArtifactView,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: CanvasFolderArtifactTabState) => ({
        canvasFolderArtifactId: tabState.get('canvasFolderArtifactId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: RunDetailsTabState,
    tabViewMetadata: {
      TabView: RunDetailsTabView,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: RunDetailsTabState) => ({
        runId: tabState.get('runId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: RunResultsTabState,
    tabViewMetadata: {
      TabView: RunResultsTabView,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: RunResultsTabState) => ({
        runId: tabState.get('runId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: SecondaryStructureTabState,
    /*
     * SecondaryStructureOptionsView renders a Jade-style template, which must be converted to React before
     * this tab can be rendered with only React. While this tab does contain DOM listeners, they appear to be
     * easy to handle in React, as all they do is open a confirmation modal.
     */
    tabViewMetadata: {TabView: SecondaryStructureView.TabView, tabViewType: BufferViewType.BACKBONE},
    /*
     * SecondaryStructureOptionsView is empty and should probably be deleted.
     */
    optionsViewMetadata: {
      OptionsView: SecondaryStructureView.OptionsView,
      optionsViewType: BufferViewType.BACKBONE,
    },
  },
  {
    tabState: SequenceLinearMapTabState,
    /*
     * Most of SequenceLinearMapTabView is still mostly rendered imperatively with d3, and that rendering is
     * delegated to a large number of child SliceViews. There is a shim for rendering each SliceView in React,
     * but the coordination would need to be migrated as well before Backbone could be removed entirely from
     * the sequence map. Additionally, the sequence map handles scroll events managed by InfiniteScroller,
     * which must be migrated to native listeners.
     */
    tabViewMetadata: {TabView: SequenceLinearMapTabView, tabViewType: BufferViewType.BACKBONE},
    /*
     * While LinearMapTabOptionsView is ultimately rendered with React, it contains a number of listeners on
     * the TabState Model, the Sequence Model, and the MapState Model, which stores state like zoom and pan.
     * Additionally, it handles VisibilityOptions, which is a Backbone Collection. All of these Backbone
     * objects would need to be ported to React before this options view could be rendered with pure React.
     */
    optionsViewMetadata: {OptionsView: LinearMapTabOptionsView, optionsViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: StorableResultsTabState,
    tabViewMetadata: {
      TabView: StorableResultsView,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: StorableResultsTabState) => ({
        storableId: tabState.get('storableId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: StorageTabState,
    /**
     * BioentityStorablesView props can easily be calculated from StorageTabState, but BioentityStorablesView
     * only fetches the actual storables on mount - it does not recalculate when the props change. If it used
     * a regular useEffect, it should be convertable to React.
     */
    tabViewMetadata: {
      TabView: StorageTabView,
      tabViewType: BufferViewType.BACKBONE,
    },
  },
  {
    tabState: LinkedResultsTabState,
    tabViewMetadata: {
      TabView: LinkedResultsView,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: LinkedResultsTabState) => ({
        fileId: tabState.get('fileId'),
        filters: tabState.get('filters'),
        resultType: tabState.get('resultType'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: StudyListingTabState,
    /**
     * StudyItemsListing props can easily be calculated from StudyListingTabState, but some aspect of its HOC
     * wrappers, probably the callback(s) passed to withSearching, cause stale data to be rendered when
     * switching back and forth between two Studies that each have StudyListingTab active.
     */
    tabViewMetadata: {
      TabView: StudyListingTabView,
      tabViewType: BufferViewType.BACKBONE,
    },
  },
  {
    tabState: StudyDesignTabState,
    tabViewMetadata: {
      TabView: StudyDesignView,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: StudyDesignTabState) => ({
        studyId: tabState.get('studyId'),
        studyType: tabState.get('studyType'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: StudyOverviewTabState,
    /*
     * StudyOverviewTabView renders with React, but contains a broad change listener on the TabState Model.
     * TabStates must be ported to Redux before ReviewTabView can be rendered with React.
     */
    tabViewMetadata: {TabView: StudyOverviewTabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: VisualizationState,
    tabViewMetadata: {
      TabView: QueryingStudyProcedureRunFlowchart,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: VisualizationState) => ({studyId: tabState.get('studyId')}),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: EntityTestOrdersTabState,
    tabViewMetadata: {
      TabView: EntityTestOrdersView,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: EntityTestOrdersTabState) => ({
        entityId: tabState.get('modelId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: ContainerTestOrdersTabState,
    tabViewMetadata: {
      TabView: ContainerTestOrdersView,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: ContainerTestOrdersTabState) => ({
        containerId: tabState.get('modelId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: StorableRelevantItemsTabState,
    tabViewMetadata: {
      TabView: StorableRelevantItemsView,
      tabViewType: BufferViewType.REACT,
      propsFromTabState: (tabState: StorableRelevantItemsTabState) => ({
        storableId: tabState.get('storableId'),
      }),
      additionalTabClassNames: 'buffer-tab--no-padding',
    },
  },
  {
    tabState: TranslationTabState,
    /*
     * TranslationTabView renders with React, but has a handful of listeners on the Sequence Model and its
     * child Models and Collections. It additionally contains significant business logic that relies on these
     * Models and Collections. In order for it to be rendered with pure React, all of these Models and
     * Collections must be ported to Redux, and the business logic must be made functional.
     */
    tabViewMetadata: {TabView: TranslationTabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: VirtualDigestTabState,
    /*
     * VirtualDigestView renders with a Jade-style template. Additionally, it extensively uses d3 to render
     * the digest SVG itself. It also has a handful of listeners on the Sequence Model and its child Models
     * and Collections. In order for it to be rendered with React, the template and digest SVG must be
     * converted to React, and the Sequence Model must be ported to Redux.
     */
    tabViewMetadata: {TabView: VirtualDigestView.TabView, tabViewType: BufferViewType.BACKBONE},
    /*
     * VirtualDigestOptionsView renders with a Jade-style template. Additionally, it contains a couple of
     * DOM listeners, and does some updates on the TabState model. In order for this options view to be
     * rendered with React, the actual rendering must be ported to React, and TabStates must be ported to
     * Redux.
     */
    optionsViewMetadata: {
      OptionsView: VirtualDigestView.OptionsView,
      optionsViewType: BufferViewType.BACKBONE,
    },
  },
  {
    tabState: VisualHistoryTabState,
    /*
     * VisualHistoryTabView renders with React, but contains a listener on the Polymer Model in order to
     * rerender when the Model changes. Additionally, it calls a function on the Sequence Model that does a
     * lot of imperative manipulation of various child Models and Collections. Protein, Sequence, and all
     * their children must be ported to Redux before VisualHistoryTabView can be rendered with pure React.
     */
    tabViewMetadata: {TabView: VisualHistoryTabView, tabViewType: BufferViewType.BACKBONE},
  },
  {
    tabState: WorksheetTabState,
    /*
     * WorksheetTabView renders with React, but contains a listener on the TabState Model. TabStates must be
     * ported to Redux before WorksheetTabView can be rendered with pure React.
     */
    tabViewMetadata: {TabView: WorksheetTabView, tabViewType: BufferViewType.BACKBONE},
  },
];

function getView(tabstate: TabState): Omit<BufferViewMetadata, 'tabState'> {
  for (const obj of TABSTATE_TO_VIEW) {
    if (tabstate instanceof obj.tabState) {
      return {
        tabViewMetadata: obj.tabViewMetadata,
        optionsViewMetadata: obj.optionsViewMetadata,
        dataTestAttrs: obj.dataTestAttrs,
      };
    }
  }
  throw new Error(`Unexpected tab state: ${tabstate.constructor.name}, please add it in buffers view`);
}

export {TABSTATE_TO_VIEW as __TABSTATE_TO_VIEW, getView};
